<template>
  <div class="w-full overflow-hidden rounded-[12px] border border-[#ebedef]">
    <!-- Headers -->
    <div
      class="w-full h-10 bg-[#f9f9fa] font-medium text-[14px] text-gray-800 border-b border-[#ebedef] flex items-center px-4"
    >
      <div class="w-1/2 pr-5 tablet-h:w-[calc(14%+25px)]">Date</div>
      <div class="hidden w-[30%] pr-5 tablet-h:block">Description</div>
      <div class="hidden w-[12%] pr-5 tablet-h:block">Total</div>
      <div class="w-1/2 pr-5 text-right tablet-h:w-[18%] tablet-h:text-left">
        Status
      </div>
      <div class="hidden w-[calc(26%-25px)] tablet-h:block" />
    </div>

    <!-- Skeleton -->
    <template v-if="showSkeleton">
      <div
        v-for="n in 3"
        :key="n"
        class="w-full h-[50px] border-b last:border-none border-[#ebedef] flex items-center justify-between px-4"
      >
        <div class="w-[calc(14%+25px)] pr-5">
          <Skeleton class="w-[80px] h-4" />
        </div>
        <div class="w-[30%] pr-5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-[12%] pr-5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-[18%] pr-5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-[calc(26%-25px)]" />
      </div>
    </template>

    <div
      v-else-if="!props.invoices?.length"
      class="text-[14px] text-gray-800 h-[100px] flex items-center justify-center"
    >
      You haven't made any payments
    </div>

    <!-- Invoice rows -->
    <template v-else>
      <div
        v-for="invoice in props.invoices"
        :key="invoice.id"
        class="text-[14px] text-gray-800 tablet-h:h-[50px] px-4 pt-[14px] pb-[18px] tablet-h:py-0 border-b last:border-none border-[#ebedef] flex flex-col justify-center"
      >
        <div
          class="flex justify-between tablet-h:[justify-content:initial] items-start tablet-h:items-center"
        >
          <div class="w-1/2 pr-5 tablet-h:w-[calc(14%+25px)]">
            <div class="w-fit">
              <button
                v-if="!isInvoiceRequestProcessing(invoice)"
                type="button"
                class="flex cursor-pointer items-center gap-1.5 text-green-700 hover:text-green-600"
                @click="handleClick(invoice)"
              >
                {{ formatInvoiceDate(invoice.date) }}
                <ExternalLinkIcon class="size-[13px]" />
              </button>
              <div v-else class="flex items-center">
                <Spinner :size="16" class="mr-2" />
                <div class="opacity-50">Loading invoice</div>
              </div>
            </div>
            <div class="mt-1.5 flex items-center tablet-h:hidden">
              <div class="mr-2 whitespace-nowrap">
                {{ invoice.description }}
              </div>
              <div>{{ formatInvoiceTotal(invoice) }}</div>
            </div>
          </div>
          <div class="hidden w-[30%] pr-5 leading-[16px] tablet-h:block">
            {{ invoice.description }}
          </div>
          <div class="hidden w-[12%] pr-5 leading-[16px] tablet-h:block">
            {{ formatInvoiceTotal(invoice) }}
          </div>
          <div
            class="flex w-1/2 items-center justify-end pr-5 tablet-h:w-[18%] tablet-h:justify-start"
          >
            <Pill
              :variant="isInvoiceOverdue(invoice) ? 'danger' : 'success'"
              :icon="isInvoiceOverdue(invoice) ? AlertIcon : CheckmarkIcon"
            >
              {{ getInvoiceText(invoice) }}
            </Pill>
          </div>
          <div
            v-if="shouldShowUpdateBilling(invoice)"
            class="hidden w-[calc(26%-25px)] justify-end tablet-h:flex"
          >
            <Button
              variant="stroke"
              size="md"
              :disabled="disableButton"
              @click="handlePayClick"
            >
              Update billing
            </Button>
          </div>
        </div>
        <Button
          v-if="shouldShowUpdateBilling(invoice)"
          class="w-full! mt-[15px] tablet-h:hidden"
          variant="stroke"
          size="lg"
          :disabled="disableButton"
          @click="handlePayClick"
        >
          Update billing
        </Button>
      </div>
    </template>

    <!-- View more -->
    <div
      v-if="showMoreInvoicesButton"
      class="w-full h-10 tablet-h:h-[38px] flex items-center justify-center text-[14px] text-link"
      :class="viewMoreClasses"
      @click="handleViewMoreClick"
    >
      <Spinner v-if="viewMoreLoading" :size="12" class="mr-2" />
      <template v-else> View more billing history </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  InvoicePdfDocument,
  InvoicePdfStatusEnum,
  InvoiceStatus,
  UserPermission,
  type Invoice,
  type InvoicePdfQuery,
  type InvoicePdfQueryVariables,
  type InvoicePdfResult,
} from '@/shared/graphql/legacy/generated'
import { Pill, Skeleton, Button } from '@dacr/library'
import { ExternalLinkIcon, CheckmarkIcon, AlertIcon } from '@dacr/icons'
import { computed, ref, watch } from 'vue'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useUserStore } from '@/shared/stores/user'
import { formatPrice } from '@/shared/utils/money'
import { apolloClient } from '@/shared/graphql/client'
import { delay } from '@/shared/utils/delay'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import Spinner from '@/shared/components/Spinner.vue'
import { useBillingStore } from '@/features/billing/store'
import { openModal, closeModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

const props = defineProps<{
  invoices?: Invoice[] | null
  hasMore: boolean
}>()

const emit = defineEmits(['fetchMore'])

const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const invoiceIdToQueryResponse = ref<Record<string, InvoicePdfResult>>({})
const viewMoreLoading = ref(false)

watch(props, () => {
  if (viewMoreLoading.value) {
    viewMoreLoading.value = false
  }
})

const showSkeleton = computed(
  () => props.invoices === null || props.invoices === undefined
)

const disableButton = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Payments)
)

const user = computed(() => userStore.user)

const formatInvoiceDate = (timestamp: number) => {
  if (!user.value) return
  return formatTimestamp(timestamp, { formatString: user.value.dateFormat })
}

const formatInvoiceTotal = (invoice: Invoice) => {
  return formatPrice({
    amount: invoice.total / 100,
    currency: invoice.currency,
  })
}

const isInvoicePaid = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Paid

const isInvoiceExpired = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Expired

const isInvoiceOverdue = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Overdue

const showMoreInvoicesButton = computed(
  () => props.invoices !== undefined && props.invoices !== null && props.hasMore
)

const handleViewMoreClick = () => {
  viewMoreLoading.value = true
  emit('fetchMore')
}

const shouldShowUpdateBilling = (invoice: Invoice) => {
  const index = (props.invoices || []).findIndex((inv) => inv.id === invoice.id)
  return !isInvoicePaid(invoice) && !isInvoiceExpired(invoice) && index === 0
}

const isInvoiceRequestProcessing = (invoice: Invoice) => {
  return (
    invoiceIdToQueryResponse.value[invoice.id] &&
    invoiceIdToQueryResponse.value[invoice.id].status ===
      InvoicePdfStatusEnum.Processing
  )
}

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return openDialog(Dialog.CantPay)
  }

  openModal(Modals.Pay, {
    events: {
      success() {
        openModal(Modals.Success, {
          props: { title: 'Payment successful' },
          events: {
            success() {
              closeModal()
            },
          },
        })
      },
    },
  })
}

const viewMoreClasses = computed(() => ({
  'cursor-pointer': !viewMoreLoading.value,
  'pointer-events-none': viewMoreLoading.value,
}))

const getInvoiceText = (invoice: Invoice) => {
  if (invoice.status === InvoiceStatus.Paid) return 'Paid'
  if (invoice.status === InvoiceStatus.Expired) return 'Paid late'
  return 'Payment overdue'
}

const handleClick = async (invoice: Invoice) => {
  if (invoiceIdToQueryResponse.value[invoice.id]?.url) {
    const url = invoiceIdToQueryResponse.value[invoice.id].url as string
    if (webviewStore.isActive) {
      webviewStore.postMessage({ type: NativeActionsEnum.LinkOpen, data: url })
    } else {
      window.open(url, '_blank')
    }
    return
  }

  invoiceIdToQueryResponse.value[invoice.id] = {
    __typename: 'InvoicePdfResult',
    url: '',
    status: InvoicePdfStatusEnum.Processing,
  }

  do {
    const response = await apolloClient.query<
      InvoicePdfQuery,
      InvoicePdfQueryVariables
    >({
      query: InvoicePdfDocument,
      variables: { invoiceId: invoice.id },
      fetchPolicy: 'network-only',
    })

    if (
      response.data.invoicePdf.status === InvoicePdfStatusEnum.Finished &&
      response.data.invoicePdf.url
    ) {
      invoiceIdToQueryResponse.value[invoice.id] = {
        __typename: 'InvoicePdfResult',
        url: response.data.invoicePdf.url,
        status: InvoicePdfStatusEnum.Finished,
      }

      const pdfUrl = invoiceIdToQueryResponse.value[invoice.id].url as string
      if (webviewStore.isActive) {
        webviewStore.postMessage({
          type: NativeActionsEnum.LinkOpen,
          data: pdfUrl,
        })
      } else {
        setTimeout(() => {
          window.open(pdfUrl, '_blank')
        })
      }
      return
    } else {
      await delay(3000)
    }
  } while (
    invoiceIdToQueryResponse.value[invoice.id].status ===
    InvoicePdfStatusEnum.Processing
  )
}
</script>
